import React, { useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from 'styled-components';
import { UnstyledButton } from './Button';
import VipIcon from '../Icons/VipIcons';
import { Avatar } from './index';

// Tagged Creators Display Components
const TaggedCreatorsSection = styled.div`
  margin: 6px 0;
  padding: 8px;
  background: ${({theme}) => theme.colors.backgroundSecondary || theme.colors.background2 || '#f8f9fa'};
  border-radius: 12px;
  border: 1px solid ${({theme}) => theme.colors.border || '#e1e5e9'};
  box-shadow: 0 1px 3px ${({theme}) => theme.colors.shadow || 'rgba(0, 0, 0, 0.05)'};
`;

const TaggedCreatorsList = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  scroll-behavior: smooth;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  /* Add subtle fade effect at edges */
  &::before,
  &::after {
    content: '';
    position: sticky;
    width: 8px;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, ${({theme}) => theme.colors.background2 || '#f8f9fa'}, transparent);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, ${({theme}) => theme.colors.background2 || '#f8f9fa'}, transparent);
  }
`;

const TaggedCreatorChip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${({theme}) => theme.colors.background || '#ffffff'};
  border: 1.5px solid ${({theme}) => theme.colors.border || '#e1e5e9'};
  border-radius: 16px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${({theme}) => theme.colors.text || '#333333'};
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px ${({theme}) => theme.colors.shadow || 'rgba(0, 0, 0, 0.1)'};
  
  &:hover {
    background: ${({theme}) => theme.colors.backgroundHover || theme.colors.background2 || '#f8f9fa'};
    border-color: ${({theme}) => theme.colors.primary || '#007bff'};
    transform: translateY(-1px);
    box-shadow: 0 2px 6px ${({theme}) => theme.colors.shadow || 'rgba(0, 0, 0, 0.15)'};
  }
  
  /* Tab-like appearance */
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    height: 3px;
    border-radius: 16px 16px 0 0;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const RemoveCreatorButton = styled(UnstyledButton)`
  background: ${({theme}) => theme.colors.backgroundSecondary || theme.colors.background2 || '#f8f9fa'};
  border: 1px solid ${({theme}) => theme.colors.border || '#e1e5e9'};
  border-radius: 50%;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transition: all 0.2s ease;
  margin-left: 2px;
  
  &:hover {
    background: ${({theme}) => theme.colors.primaryBlueLight};
    border-color: ${({theme}) => theme.colors.primaryBlueLight};
    color: white;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AddCreatorButton = styled(UnstyledButton)`
  background: ${({theme}) => theme.colors.background || '#ffffff'};
  border: 1.5px dashed ${({theme}) => theme.colors.border || '#e1e5e9'};
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: ${({theme}) => theme.colors.textSecondary || '#666'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({theme}) => theme.colors.backgroundHover || '#f8f9fa'};
    border-color: ${({theme}) => theme.colors.primary || '#007bff'};
    color: ${({theme}) => theme.colors.primary || '#007bff'};
  }
`;

/**
 * Common TaggedCreators component for displaying selected creators
 * Used in both CreatePost and MessageInputArea for consistency
 * 
 * @param {Array} creators - List of creator objects to display
 * @param {Function} onRemoveCreator - Callback when remove button is clicked (optional for read-only)
 * @param {Function} onAddCreator - Callback when add button is clicked (optional)
 * @param {boolean} readOnly - If true, hide remove buttons
 * @param {boolean} showAddButton - If true, show add button at the end
 */
const TaggedCreators = ({
  creators = [],
  onRemoveCreator,
  onAddCreator,
  readOnly = false,
  showAddButton = false,
  showTitle = false,
  title = "Tagged Creators"
}) => {
  const theme = useContext(ThemeContext);

  if ((!creators || creators.length === 0) && !showAddButton) {
    return null;
  }

  return (
    <TaggedCreatorsSection>
      <TaggedCreatorsList>
        {creators.map(creator => (
          <TaggedCreatorChip key={creator.id}>
            <Avatar 
              src={creator.profilePicture || creator.profilePictures?.thumbnail} 
              name={creator.fullName || creator.username}
              size="16px"
              userId={creator.id}
              style={{ marginRight: '2px' }}
              hideOnlineIndicator={true}
            />
            {creator.fullName || creator.username}
            {(creator.isVerified || creator.verified) && (
              <VipIcon 
                name="VerifiedBadge" 
                size={14} 
                style={{ marginLeft: '2px' }}
              />
            )}
            {!readOnly && onRemoveCreator && (
              <RemoveCreatorButton 
                onClick={() => onRemoveCreator(creator.id)}
                title="Remove tagged creator"
              >
                <VipIcon name="CloseXCircle" size={14} stroke={theme.colors.primaryDark}/>
              </RemoveCreatorButton>
            )}
          </TaggedCreatorChip>
        ))}
        {showAddButton && onAddCreator && (
          <AddCreatorButton onClick={onAddCreator} title="Tag creators">
            <VipIcon name="Add" size={14} />
            Tag
          </AddCreatorButton>
        )}
      </TaggedCreatorsList>
    </TaggedCreatorsSection>
  );
};

export default TaggedCreators;
